import { useEffect, lazy, Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { AuthProvider } from "@/contexts/AuthContext";
import PageTransition from "./components/PageTransition";
import CanonicalTag from "./components/CanonicalTag";
import Index from "./pages/Index";

// Lazy load non-critical route components for code splitting
const AllProjects = lazy(() => import("./pages/AllProjects"));
const LocalSeoProject = lazy(() => import("./pages/LocalSeoProject"));
const EcommerceSeoProject = lazy(() => import("./pages/EcommerceSeoProject"));
const TechnicalSeoProject = lazy(() => import("./pages/TechnicalSeoProject"));
const ContentSeoProject = lazy(() => import("./pages/ContentSeoProject"));
const InternationalSeoProject = lazy(() => import("./pages/InternationalSeoProject"));
const SeoAnalyticsProject = lazy(() => import("./pages/SeoAnalyticsProject"));
const Blog = lazy(() => import("./pages/Blog"));
const BlogPost = lazy(() => import("./pages/BlogPost"));
const Contact = lazy(() => import("./pages/Contact"));
const Auth = lazy(() => import("./pages/Auth"));
const AdminDashboard = lazy(() => import("./pages/AdminDashboard"));
const NotFound = lazy(() => import("./pages/NotFound"));

// Service Pages
const LocalSeoService = lazy(() => import("./pages/services/LocalSeoService"));
const YoutubeSeoService = lazy(() => import("./pages/services/YoutubeSeoService"));
const EcommerceSeoService = lazy(() => import("./pages/services/EcommerceSeoService"));
const MetaAdsService = lazy(() => import("./pages/services/MetaAdsService"));
const DigitalMarketingService = lazy(() => import("./pages/services/DigitalMarketingService"));

const queryClient = new QueryClient();

// Scroll to top on route change
const ScrollToTopOnNavigate = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
};

const AppRoutes = () => {
  const location = useLocation();
  
  return (
    <>
      <ScrollToTopOnNavigate />
      <CanonicalTag />
      <Suspense fallback={<div className="min-h-screen bg-background flex items-center justify-center"><div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" /></div>}>
        <PageTransition key={location.pathname}>
          <Routes location={location}>
            <Route path="/" element={<Index />} />
            <Route path="/auth" element={<Auth />} />
            <Route path="/admin" element={<AdminDashboard />} />
            <Route path="/projects" element={<AllProjects />} />
            <Route path="/projects/local-seo" element={<LocalSeoProject />} />
            <Route path="/projects/ecommerce-seo" element={<EcommerceSeoProject />} />
            <Route path="/projects/technical-seo" element={<TechnicalSeoProject />} />
            <Route path="/projects/content-seo" element={<ContentSeoProject />} />
            <Route path="/projects/international-seo" element={<InternationalSeoProject />} />
            <Route path="/projects/seo-analytics" element={<SeoAnalyticsProject />} />
            <Route path="/blog" element={<Blog />} />
            <Route path="/blog/:slug" element={<BlogPost />} />
            <Route path="/contact" element={<Contact />} />
            {/* Service Pages */}
            <Route path="/services/local-seo" element={<LocalSeoService />} />
            <Route path="/services/youtube-seo" element={<YoutubeSeoService />} />
            <Route path="/services/ecommerce-seo" element={<EcommerceSeoService />} />
            <Route path="/services/meta-ads" element={<MetaAdsService />} />
            <Route path="/services/digital-marketing" element={<DigitalMarketingService />} />
            {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
            <Route path="*" element={<NotFound />} />
          </Routes>
        </PageTransition>
      </Suspense>
    </>
  );
};

const App = () => (
  <HelmetProvider>
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <TooltipProvider>
          <Toaster />
          <Sonner />
          <BrowserRouter>
            <AppRoutes />
          </BrowserRouter>
        </TooltipProvider>
      </AuthProvider>
    </QueryClientProvider>
  </HelmetProvider>
);

export default App;
